import dayjs from 'dayjs';
import z from 'zod';

import { EMAIL_REGEX } from './schema';

const dateFormats = [
  'YYYY-MM-DD', // e.g., 2025-07-20
  'DD/MM/YYYY', // e.g., 20/07/2025
  'DD-MM-YYYY', // e.g., 20-07-2025
  'DD.MM.YYYY', // e.g., 20.07.2025
  'MM/DD/YYYY', // e.g., 07/20/2025
  'MM-DD-YYYY', // e.g., 07-20-2025
  'MMMM DD, YYYY', // e.g., July 20, 2025
  'DD MMMM YYYY', // e.g., 20 July 2025
  'MMM DD, YYYY', // e.g., Jul 20, 2025
  'YYYY', // e.g., 2025
];

export const ParsedMainFormSchema = z.object({
  description: z.string().min(3, { message: 'Must be more than 3 characters' }),
  email: z
    .union([z.literal(''), z.string().email({ message: 'Must be a valid email' })])
    .optional(),
  jobTitle: z.string().min(3, { message: 'Must be more than 3 characters' }),
  location: z.string().optional(),
  phoneNumber: z
    .union([
      z.literal(''),
      z.string().max(20, { message: 'Phone number must be at most 20 characters' }),
    ])
    .optional(),
  profileName: z
    .string()
    .min(3, { message: 'Must be more than 3 characters' })
    .refine((value) => !EMAIL_REGEX.test(value), 'Name cannot be an email address'),
  totalExperience: z.coerce
    .number({
      invalid_type_error: 'Experience must be a number',
      required_error: 'Experience is required',
    })
    .positive({ message: 'Years of experience must be greater than 0' })
    .transform((total) => total.toString()),
});

const parsedDateStringSchema = z.string().refine(
  (val) => {
    for (const format of dateFormats) {
      if (dayjs(val, format, true).isValid()) {
        return true;
      }
    }
    return dayjs(val).isValid();
  },
  {
    message: `Invalid date format. Expected formats: ${dateFormats.join(', ')}`,
  },
);

export const ParsedEducationFormSchema = z
  .object({
    degree: z.string().min(1, 'Level is required'),
    description: z.string().optional(),
    end: parsedDateStringSchema.nullable(),
    id: z.string().optional(),
    location: z.string().nullable().optional(),
    ongoing: z.boolean(),
    program: z.string().optional(),
    school: z.string().min(1, 'School name is required'),
    start: parsedDateStringSchema.refine((date) => {
      return dayjs(date).isBefore(dayjs());
    }, 'The Start date must be before today'),
  })
  .required()
  .refine(
    (schema) => {
      if (schema.ongoing) {
        return true;
      }

      if (
        (dayjs(schema.start).isBefore(dayjs(schema.end)) && dayjs(schema.end).isBefore(dayjs())) ||
        !schema.end
      ) {
        return true;
      }

      return false;
    },
    {
      message: 'The End date must be between the Start date and today',
      path: ['end'],
    },
  );

export const ParsedExperienceFormSchema = z
  .object({
    company: z.string().min(1, 'Company name is required'),
    description: z.string().optional(),
    end: parsedDateStringSchema.nullable().optional(),
    id: z.string().optional(),
    location: z.string().nullable().optional(),
    ongoing: z.boolean().optional(),
    start: parsedDateStringSchema.refine((date) => {
      return dayjs(date).isBefore(dayjs());
    }, 'The Start date must be before today'),
    title: z.string().min(1, 'Title is required'),
  })
  .required()
  .refine(
    (schema) => {
      if (schema.ongoing) {
        return true;
      }

      if (
        (dayjs(schema.start).isBefore(dayjs(schema.end)) && dayjs(schema.end).isBefore(dayjs())) ||
        !schema.end
      ) {
        return true;
      }

      return false;
    },
    {
      message: 'The End date must be between the Start date and today',
      path: ['end'],
    },
  );

export const ParsedLanguageFormSchema = z
  .object({
    language: z.string().optional(),
    rate: z.union([z.string(), z.number()]).optional(),
  })
  .optional();

export const ParseRateFormSchema = z
  .object({
    hourlyRate: z.string().nullish().optional(),
    minimumHourlyRate: z.string().nullish().optional(),
    minimumMonthlyRate: z.string().nullish().optional(),
    monthlyRate: z.string().nullish().optional(),
    rateCurrency: z.string().nullish().optional(),
  })
  .partial()
  .optional();

export const ParsedSkillFormSchema = z
  .object({
    id: z.string().optional(),
    rate: z.union([z.string(), z.number()]).optional(),
    skill: z.string().optional(),
  })
  .optional();
